import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Mail, Clock, CheckCircle, XCircle, Search, ChevronDown, ChevronRight, Users, X } from 'lucide-react'
import api from '../../services/api'

export default function EmailHistoryGrouped() {
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [expandedBatches, setExpandedBatches] = useState(new Set())
  const [expandedEmails, setExpandedEmails] = useState(new Set())

  // Récupérer les batches
  const { data: batchesData, isLoading: batchesLoading } = useQuery({
    queryKey: ['emailBatches'],
    queryFn: async () => {
      const response = await api.get('/api/communication/batches')
      return response.data
    }
  })

  // Récupérer les emails individuels (non bulk)
  const { data: individualsData, isLoading: individualsLoading } = useQuery({
    queryKey: ['emailHistory', statusFilter],
    queryFn: async () => {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : ''
      const response = await api.get(`/api/communication/history${params}`)
      return response.data
    }
  })

  // Récupérer les statistiques
  const { data: statsData } = useQuery({
    queryKey: ['emailStats'],
    queryFn: async () => {
      const response = await api.get('/api/communication/history/stats')
      return response.data
    }
  })

  const batches = batchesData?.batches || []
  const individualEmails = (individualsData?.emails || []).filter(e => !e.isBulk)
  const stats = statsData?.stats || { total: 0, sent: 0, failed: 0, pending: 0 }
  const todayCount = statsData?.todayCount || 0

  const isLoading = batchesLoading || individualsLoading

  // Filtrer par recherche
  const filteredBatches = batches.filter(batch => {
    if (!searchQuery.trim()) return true
    const search = searchQuery.toLowerCase()
    return (
      batch.subject?.toLowerCase().includes(search) ||
      batch.campaignName?.toLowerCase().includes(search) ||
      batch.emails.some(e => 
        e.recipientEmail?.toLowerCase().includes(search) ||
        e.recipientName?.toLowerCase().includes(search)
      )
    )
  })

  const filteredIndividuals = individualEmails.filter(email => {
    if (!searchQuery.trim()) return true
    const search = searchQuery.toLowerCase()
    return (
      email.recipientEmail?.toLowerCase().includes(search) ||
      email.recipientName?.toLowerCase().includes(search) ||
      email.subject?.toLowerCase().includes(search) ||
      email.campaignName?.toLowerCase().includes(search)
    )
  })

  const toggleBatch = (batchId) => {
    const newExpanded = new Set(expandedBatches)
    if (newExpanded.has(batchId)) {
      newExpanded.delete(batchId)
    } else {
      newExpanded.add(batchId)
    }
    setExpandedBatches(newExpanded)
  }

  const toggleEmail = (emailId) => {
    const newExpanded = new Set(expandedEmails)
    if (newExpanded.has(emailId)) {
      newExpanded.delete(emailId)
    } else {
      newExpanded.add(emailId)
    }
    setExpandedEmails(newExpanded)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent':
        return <CheckCircle size={16} style={{ color: '#5b5cf4' }} />
      case 'failed':
        return <XCircle size={16} style={{ color: '#ef4444' }} />
      case 'pending':
        return <Clock size={16} style={{ color: '#ffcc00' }} />
      default:
        return null
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'À l\'instant'
    if (minutes < 60) return `Il y a ${minutes}min`
    if (hours < 24) return `Il y a ${hours}h`
    if (days < 7) return `Il y a ${days}j`
    
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2)
    const h = String(date.getHours()).padStart(2, '0')
    const m = String(date.getMinutes()).padStart(2, '0')
    const s = String(date.getSeconds()).padStart(2, '0')
    return `${day}/${month}/${year} ${h}:${m}:${s}`
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
        <div className="spinning" style={{ 
          width: 48, 
          height: 48, 
          border: '4px solid rgba(91, 92, 244,0.1)', 
          borderTop: '4px solid #5b5cf4', 
          borderRadius: '50%', 
          margin: '0 auto 1rem' 
        }}></div>
        Chargement de l'historique...
      </div>
    )
  }

  return (
    <div>
      {/* Statistiques */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'rgb(var(--rgb-ink) / 0.02)',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            Total envoyé
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-text)' }}>
            {stats.total}
          </div>
        </div>

        <div style={{
          background: 'rgb(var(--rgb-ink) / 0.02)',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            Réussis
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-text)' }}>
            {stats.sent}
          </div>
        </div>

        <div style={{
          background: 'rgb(var(--rgb-ink) / 0.02)',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            Échoués
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-text)' }}>
            {stats.failed}
          </div>
        </div>

        <div style={{
          background: 'rgb(var(--rgb-ink) / 0.02)',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
            Aujourd'hui
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-text)' }}>
            {todayCount}
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div style={{ marginBottom: '2rem', position: 'relative' }}>
        <Search
          size={20}
          style={{
            position: 'absolute',
            left: '1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--color-text-secondary)'
          }}
        />
        <input
          type="text"
          placeholder="Rechercher dans l'historique..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            background: 'rgb(var(--rgb-ink) / 0.05)',
            border: '1px solid rgb(var(--rgb-ink) / 0.1)',
            borderRadius: '8px',
            padding: '0.75rem 1rem 0.75rem 3rem',
            color: 'var(--color-text)',
            fontSize: '0.875rem'
          }}
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Liste combinée */}
      {filteredBatches.length === 0 && filteredIndividuals.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgb(var(--rgb-ink) / 0.02)',
          borderRadius: '12px',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)'
        }}>
          <Mail size={48} style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }} />
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem' }}>
            {searchQuery ? 'Aucun email trouvé' : 'Aucun email envoyé pour le moment'}
          </p>
        </div>
      ) : (
        <div style={{
          background: 'rgb(var(--rgb-ink) / 0.02)',
          borderRadius: '12px',
          border: '1px solid rgb(var(--rgb-ink) / 0.1)',
          overflow: 'hidden'
        }}>
          {/* Batches (envois en masse) */}
          {filteredBatches.map((batch, index) => {
            const isExpanded = expandedBatches.has(batch.id)
            const successRate = batch.totalEmails > 0 
              ? Math.round((batch.sentEmails / batch.totalEmails) * 100) 
              : 0

            return (
              <div
                key={`batch-${batch.id}`}
                style={{
                  borderBottom: '1px solid rgb(var(--rgb-ink) / 0.05)'
                }}
              >
                {/* Batch Header */}
                <div
                  onClick={() => toggleBatch(batch.id)}
                  style={{
                    padding: '1rem 1.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'background 0.2s',
                    background: isExpanded ? 'rgba(91, 92, 244,0.03)' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!isExpanded) e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.02)'
                  }}
                  onMouseLeave={(e) => {
                    if (!isExpanded) e.currentTarget.style.background = 'transparent'
                  }}
                >
                  {/* Chevron */}
                  <div style={{ 
                    color: isExpanded ? '#5b5cf4' : 'var(--color-text-secondary)',
                    transition: 'all 0.3s'
                  }}>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </div>

                  {/* Icon */}
                  <div style={{ flexShrink: 0 }}>
                    <Users size={20} style={{ color: '#5b5cf4' }} />
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: 600, 
                      color: 'var(--color-text)',
                      fontSize: '0.9375rem',
                      marginBottom: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      flexWrap: 'wrap'
                    }}>
                      <span>Envoi en masse</span>
                      <span style={{
                        background: 'rgba(91, 92, 244,0.1)',
                        border: '1px solid rgba(91, 92, 244,0.3)',
                        borderRadius: '12px',
                        padding: '0.125rem 0.5rem',
                        fontSize: '0.75rem',
                        color: '#5b5cf4'
                      }}>
                        {batch.totalEmails} email{batch.totalEmails > 1 ? 's' : ''}
                      </span>
                      {batch.campaignName && (
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                          {batch.campaignName}
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      color: 'var(--color-text-secondary)', 
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {batch.subject}
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '0.25rem',
                    minWidth: '150px'
                  }}>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                      {formatDate(batch.createdAt)}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem' }}>
                      <span style={{ color: '#5b5cf4' }}>
                        ✓ {batch.sentEmails}
                      </span>
                      {batch.failedEmails > 0 && (
                        <span style={{ color: '#ef4444' }}>
                          ✗ {batch.failedEmails}
                        </span>
                      )}
                      <span style={{ color: 'var(--color-text-secondary)' }}>
                        ({successRate}%)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Batch Emails (Expanded) */}
                <div
                  style={{
                    maxHeight: isExpanded ? '2000px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease, opacity 0.3s ease',
                    opacity: isExpanded ? 1 : 0
                  }}
                >
                  <div style={{
                    padding: '0 1.5rem 1rem 4rem',
                    background: 'rgb(var(--rgb-shade) / 0.15)'
                  }}>
                    {batch.emails.map((email, emailIndex) => {
                      const isEmailExpanded = expandedEmails.has(`batch-${batch.id}-email-${email.id}`)
                      
                      return (
                        <div
                          key={email.id}
                          style={{
                            borderBottom: emailIndex < batch.emails.length - 1 ? '1px solid rgb(var(--rgb-ink) / 0.03)' : 'none',
                            paddingTop: emailIndex === 0 ? '1rem' : '0.75rem',
                            paddingBottom: '0.75rem'
                          }}
                        >
                          {/* Email Header */}
                          <div
                            onClick={() => toggleEmail(`batch-${batch.id}-email-${email.id}`)}
                            style={{
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.5rem',
                              borderRadius: '6px',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.02)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ color: 'var(--color-text-muted)' }}>
                              {isEmailExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </div>
                            <div>{getStatusIcon(email.status)}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '0.875rem', color: '#5b5cf4' }}>
                                {email.recipientEmail}
                                {email.recipientName && (
                                  <span style={{ color: 'var(--color-text-secondary)', marginLeft: '0.5rem' }}>
                                    ({email.recipientName})
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                              {formatDate(email.sentAt)}
                            </div>
                          </div>

                          {/* Email Details */}
                          {isEmailExpanded && (
                            <div style={{
                              marginTop: '0.5rem',
                              padding: '0.75rem',
                              background: 'rgb(var(--rgb-shade) / 0.3)',
                              borderRadius: '6px',
                              fontSize: '0.8125rem',
                              color: 'var(--color-text-secondary)',
                              maxHeight: '150px',
                              overflow: 'auto',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {email.body}
                              {email.errorMessage && (
                                <div style={{
                                  marginTop: '0.5rem',
                                  padding: '0.5rem',
                                  background: 'rgba(239,68,68,0.1)',
                                  border: '1px solid rgba(239,68,68,0.2)',
                                  borderRadius: '4px',
                                  color: '#ef4444'
                                }}>
                                  Erreur: {email.errorMessage}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )
          })}

          {/* Individual Emails */}
          {filteredIndividuals.map((email) => {
            const isExpanded = expandedEmails.has(`individual-${email.id}`)

            return (
              <div
                key={`individual-${email.id}`}
                style={{
                  borderBottom: '1px solid rgb(var(--rgb-ink) / 0.05)'
                }}
              >
                {/* Email Header */}
                <div
                  onClick={() => toggleEmail(`individual-${email.id}`)}
                  style={{
                    padding: '1rem 1.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    transition: 'background 0.2s',
                    background: isExpanded ? 'rgba(91, 92, 244,0.03)' : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!isExpanded) e.currentTarget.style.background = 'rgb(var(--rgb-ink) / 0.02)'
                  }}
                  onMouseLeave={(e) => {
                    if (!isExpanded) e.currentTarget.style.background = 'transparent'
                  }}
                >
                  <div style={{ color: isExpanded ? '#5b5cf4' : 'var(--color-text-secondary)' }}>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </div>
                  <div>{getStatusIcon(email.status)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: 600, 
                      color: '#5b5cf4',
                      fontSize: '0.9375rem',
                      marginBottom: '0.25rem'
                    }}>
                      {email.recipientEmail}
                      {email.recipientName && (
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                          ({email.recipientName})
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      color: 'var(--color-text-secondary)', 
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {email.subject}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                    {formatDate(email.sentAt)}
                  </div>
                </div>

                {/* Email Details */}
                {isExpanded && (
                  <div style={{
                    padding: '1rem 1.5rem 1.5rem 3.5rem',
                    background: 'rgb(var(--rgb-shade) / 0.2)'
                  }}>
                    <div style={{
                      color: 'var(--color-text-secondary)',
                      fontSize: '0.875rem',
                      background: 'rgb(var(--rgb-shade) / 0.3)',
                      padding: '1rem',
                      borderRadius: '6px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {email.body}
                    </div>
                    {email.errorMessage && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '0.75rem',
                        background: 'rgba(239,68,68,0.1)',
                        border: '1px solid rgba(239,68,68,0.2)',
                        borderRadius: '6px',
                        color: '#ef4444',
                        fontSize: '0.875rem'
                      }}>
                        Erreur: {email.errorMessage}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
